import { Box, Button, Typography } from '@mui/material';
import { JSX, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRecordDateTime } from '../../../library/dateTimeHelper';
import { IAppointment } from '../../../library/models/appointment';
import { localised } from '../../../library/models/localisedText';
import { splitApiError } from '../../../library/useApiFormError';
import { useRefreshDiary } from '../../../library/useRefreshDiary';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactInfiniteQuery, useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import { DeclineRequestDialog } from '../../appointmentDetail/DeclineRequestDialog';
import { useSnackbar } from '../../layout/snackbarProvider';
import { PersonLabel } from '../../userAvatar/UserAvatar';
import {
    pendingActionsStyles,
    pendingBoxStyles,
    pendingEmbeddedBoxStyles,
    pendingDetailStyles,
    pendingRespondByStyles,
    pendingRowStyles,
    pendingShowMoreStyles,
    pendingSubtitleStyles,
    pendingTitleStyles,
    pendingWhenStyles,
} from './pendingRequests.styles';

/** How many requests a page holds. A therapist rarely has more; "show more" fetches the rest. */
export const PENDING_PAGE_SIZE = 5;

/**
 * The patients waiting for this therapist's answer, above their calendar.
 *
 * The same requests are in the grid, drawn in outline on their own hours -- but a request for
 * next month is not in the week on screen, and one lapses unanswered three hours before it
 * starts. So they are also listed here, soonest first, which is the order they lapse in, with
 * the answer on each row: the grid shows *where* a request sits, this shows *what is owed*.
 *
 * **Paged on the server and pinned to the caller there** -- `GET /appointments/requests` takes
 * no therapist, since only the therapist a request names may answer it. A request past its
 * deadline is already left out. The strip is absent when there is nothing to answer, rather
 * than an empty box asking for attention.
 *
 * The rows are not buttons: each carries two, and a row is a button or contains one, never
 * both. The calendar card is where the whole appointment opens.
 *
 * **The bell in the header shows the same strip** (`embedded`), without the frame it wears
 * above the calendar, since it sits inside a panel that already has one. Both can be on
 * screen at once -- they share one query and each keeps its own decline dialog -- which is
 * why the heading's id is generated rather than fixed.
 */
export interface IPendingRequestsProps {
    /** Inside another panel: no frame and no margin of its own. */
    embedded?: boolean;
}

export const PendingRequests = ({ embedded = false }: IPendingRequestsProps): JSX.Element | null => {
    const titleId: string = useId();
    const { t: translate, i18n } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const refreshDiary = useRefreshDiary();

    const [declining, setDeclining] = useState<IAppointment | null>(null);

    const { data, hasNextPage, fetchNextPage, isFetchingNextPage } =
        useReactInfiniteQuery<IAppointment>(
            services.appointments.getPendingRequests(PENDING_PAGE_SIZE)
        );

    const confirm = useReactMutation(services.appointments.confirmRequestMutation());

    const requests: IAppointment[] = data?.pages.flatMap(page => page.items) ?? [];
    const total: number = data?.pages[0]?.totalItems ?? 0;

    const handleConfirm = (request: IAppointment): void => {
        confirm.mutate(request.id, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.APPOINTMENT_REQUEST_CONFIRMED), 'success');
                refreshDiary();
            },
            onError: (error: unknown) => showSnackbar(
                splitApiError(error, [], translate,
                    ResourcesIds.APPOINTMENT_REQUEST_ACTION_FAILED).formError
                    ?? translate(ResourcesIds.APPOINTMENT_REQUEST_ACTION_FAILED),
                'error'
            ),
        });
    };

    if (requests.length === 0) {
        return null;
    }

    return (
        <Box
            sx={embedded ? pendingEmbeddedBoxStyles : pendingBoxStyles}
            component="section"
            aria-labelledby={titleId}
        >
            <Typography id={titleId} sx={pendingTitleStyles}>
                {translate(ResourcesIds.PENDING_REQUESTS_TITLE)} ({total})
            </Typography>
            <Typography sx={pendingSubtitleStyles}>
                {translate(ResourcesIds.PENDING_REQUESTS_SUBTITLE)}
            </Typography>

            {requests.map(request => (
                <Box key={request.id} sx={pendingRowStyles} data-testid="pending-request">
                    <Box>
                        <Typography sx={pendingWhenStyles}>
                            {formatRecordDateTime(request.startsAt, i18n)}
                        </Typography>
                        <PersonLabel
                            size="sm"
                            person={{
                                name: request.patientName,
                                pictureUrl: request.patientPictureUrl,
                            }}
                        />
                        {request.serviceNameRo && (
                            <Typography sx={pendingDetailStyles}>
                                {localised(request.serviceNameRo, request.serviceNameEn, i18n.language)}
                            </Typography>
                        )}
                        {request.confirmBy && (
                            <Typography sx={pendingRespondByStyles}>
                                {translate(ResourcesIds.APPOINTMENT_REQUEST_RESPOND_BY, {
                                    when: formatRecordDateTime(request.confirmBy, i18n),
                                })}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={pendingActionsStyles}>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={confirm.isPending}
                            onClick={() => handleConfirm(request)}
                        >
                            {translate(ResourcesIds.APPOINTMENT_REQUEST_CONFIRM)}
                        </Button>
                        <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={confirm.isPending}
                            onClick={() => setDeclining(request)}
                        >
                            {translate(ResourcesIds.APPOINTMENT_REQUEST_DECLINE)}
                        </Button>
                    </Box>
                </Box>
            ))}

            {hasNextPage && (
                <Box sx={pendingShowMoreStyles}>
                    <Button disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
                        {translate(ResourcesIds.PENDING_REQUESTS_SHOW_MORE, {
                            remaining: Math.max(total - requests.length, 0),
                        })}
                    </Button>
                </Box>
            )}

            <DeclineRequestDialog
                appointment={declining}
                onClose={() => setDeclining(null)}
                onDeclined={() => {
                    showSnackbar(translate(ResourcesIds.APPOINTMENT_REQUEST_DECLINED), 'success');
                    setDeclining(null);
                    refreshDiary();
                }}
            />
        </Box>
    );
};
